import axios, { type AxiosInstance } from 'axios';

/**
 * HTTP client for the Flightdeck insights service (audience builder backend),
 * called directly from the browser rather than through the CRM.
 *
 * Auth: the user's own CRM login token goes in `Authorization: Bearer`. In
 * `crm_me` mode insights validates it against the CRM's `GET /me/permissions`
 * (cached 5 minutes) and takes the user's tenant, firm, `send_as` companies
 * and scopes from that answer, so no token exchange is needed. The browser
 * `Origin` must be one of the portals insights allows, otherwise it answers 401.
 *
 * Base URL comes from `VITE_INSIGHTS_API_URL`; without it every call fails
 * with a clear error instead of hitting a relative path on the portal.
 */

export const INSIGHTS_API_URL_ENV = 'VITE_INSIGHTS_API_URL';

export class InsightsNotConfiguredError extends Error {
  constructor() {
    super(
      `Insights service is not configured: set ${INSIGHTS_API_URL_ENV} for this environment.`
    );
    this.name = 'InsightsNotConfiguredError';
  }
}

export function getInsightsBaseUrl(): string {
  const value = (import.meta.env.VITE_INSIGHTS_API_URL as string | undefined)
    ?.trim()
    .replace(/\/+$/, '');
  return value ?? '';
}

export function createInsightsClient(
  baseURL: string = getInsightsBaseUrl()
): AxiosInstance {
  const client = axios.create({
    baseURL: baseURL || undefined,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    // Auth is a bearer token, not the CRM session cookie.
    withCredentials: false
  });

  client.interceptors.request.use((config) => {
    if (!config.baseURL) {
      return Promise.reject(new InsightsNotConfiguredError());
    }
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return client;
}
